<template>
  <div class="tw-overflow-x-hidden">
    <!-- version with v3 jeremy design -->
    <div class="tw-bg-[#252f4a] tw-text-white tw-overflow-x-auto">
      <div class="stk-p-8">
        <PageHeading titleClassname="tw-text-inherit" subtitleClassname="tw-text-inherit tw-opacity-50">
          <template v-slot:title>
            Portfolio<span class="tw-text-[#F1583D]">Intelligence</span>
          </template>
          <template v-slot:subtitle>
            AI-enhanced portfolio analysis & proposal generation
          </template>
        </PageHeading>
      </div>

      <!-- Global Optimization Error Notification -->
      <div v-if="checkupStore.optimizationError" class="tw-px-8 tw-pb-4">
        <b-notification type="is-danger" :closable="true" @close="checkupStore.optimizationError = null">
          <div class="tw-flex tw-items-center tw-gap-3">
            <i class="fa fa-times-circle tw-text-xl" />
            <div class="tw-w-full">
              <p class="tw-font-bold tw-mb-2">
                Optimization Failed
              </p>
              <p v-if="showDetailedError" class="tw-mb-0 tw-whitespace-pre-line tw-text-sm">
                {{ optimizationErrorMessage }}
              </p>
              <p v-else class="tw-mb-0 tw-text-sm">
                The optimization could not be completed with the current constraints. Please try adjusting your portfolio preferences and try again.
              </p>
            </div>
          </div>
        </b-notification>
      </div>

      <div class="tw-flex tw-gap-2 tw-p-3 tw-overflow-x-hidden">
        <!-- <div class="tw-flex tw-min-w-[200px]"> -->
        <AdvisorCoreNav @onPortfolioToggle="onSelectComplete" />
        <!-- </div> -->
        <div :class="{ 'tw-hidden': checkupStore.activeArea !== 'Start'}">
          <div class="">
            <section v-if="announcement && announcement.active" class="section">
              <div class="columns">
                <div class="column ">
                  <p v-if="announcement && announcement.active" class="notification is-danger is-light">
                    {{ announcement.text }}
                  </p>
                  <div class="tw-flex tw-items-center tw-justify-between tw-my-3">

                    <TrialPayment />
                  </div>

                  <!-- <div
                    v-if="!checkupStore.portfolio"
                    class="subtitle"
                  >
                    Analyzes 10 pages per minute
                  </div> -->
                </div>
              </div>
            </section>

            <div class="tw-flex tw-justify-center">
              <div class="tw-rounded-2xl tw-grow tw-bg-white tw-flex tw-flex-col tw-relative">
                <section class="tw-mx-12 tw-flex tw-flex-col tw-pb-10">
                  <div class="tw-grow tw-basis-0">
                    <PortfolioUploadSection @run-analysis-now="handleRunAnalysisNow" @analytics-done="onSelectComplete" />
                    <div class="tw-fixed tw-bottom-12" :class="{ 'tw-hidden': checkupStore.activeArea !== 'Start' }">
                      <RunAnalysisButton ref="runAnalysisButton" @analytics-done="onSelectComplete" />
                    </div>
                    <template v-if="checkupStore.featureFlags.includes('Feature: Account Onboarding Extraction')">
                      <p class="title" />
                      <AccountOnboardingSection @complete="onSelectComplete" />
                      </br>
                    </template>

                    <template v-if="checkupStore.featureFlags.includes('Feature: Raw Data Extraction')">
                      <p class="title" />
                      <DocumentDataExtractionSection :can-upload-pdf="true"
                        :can-manual-input="checkupStore.featureFlags.includes('Feature: OCR Support')"
                        @complete="onSelectComplete" />
                      </br>
                    </template>
                  </div>

                  <div class="tw-grow tw-basis-0" />
                </section>
              </div>
              <div>
                <AdvisorCoreInstructions />
                <AdvisorCoreFAQ />
              </div>
            </div>
          </div>
          <CheckupDisclaimer />
        </div>

        <div v-if="showSettingsTab" :class="{ 'tw-hidden': checkupStore.activeArea !== 'Settings', 'tw-w-full': true }">
          <SettingsArea />
        </div>

        <div v-if="hasHoldings" :class="{ 'tw-hidden': checkupStore.activeArea !== 'Holdings' }">
          <PortfolioHoldingsAreaContainer />
          <CheckupDisclaimer />
        </div>
        <OverviewArea v-if="checkupStore.areas?.length > 1"
          :class="{ 'tw-hidden': checkupStore.activeArea !== 'Overview' }" :areas="checkupStore.areas"
          @change-tab="handleTabChange" />


        <TaxTransitionArea :class="{ 'tw-hidden': checkupStore.activeArea !== 'Tax Transition' }" />

        <StressTestArea v-if="checkupStore.featureFlags.includes('Feature: Stress Test')" ref="stressTestArea" :class="{ 'tw-hidden': checkupStore.activeArea !== 'Stress Test' }" />

          <!-- Risk -->
          <RiskArea
            v-if="checkupStore.areas.some(area => area.name === 'Risk')"
            :class="{ 'tw-hidden': checkupStore.activeArea !== 'Risk' }" :disabled="checkupStore.noMatchedHoldings"
          />

          <!-- Executive Summary -->
          <!-- <div
          :class="{ 'tw-hidden': checkupStore.activeArea !== 'Executive Summary' }"
        >
          <ExecutiveSummaryArea />
          <CheckupDisclaimer />
        </div> -->

          <!-- Asset Location Advanced-->
          <!-- <div
          :class="{ 'tw-hidden': checkupStore.activeArea !== 'Asset Location Advanced' }"
        >
          <AssetLocationAdvancedArea />
          <CheckupDisclaimer />
        </div> -->

          <!-- Values Alignment -->
          <ValuesArea
            v-if="checkupStore.areas.some(area => area.name === 'Values Alignment')"
            :class="{ 'tw-hidden': checkupStore.activeArea !== 'Values Alignment' }"
            :disabled="checkupStore.noMatchedHoldings"
          />

          <!-- Asset Class Breakdown -->
          <AssetClassArea
            v-if="checkupStore.areas.some(area => area.name === 'Asset Class Breakdown')"
            :class="{ 'tw-hidden': checkupStore.activeArea !== 'Asset Class Breakdown' }"
            :disabled="checkupStore.noMatchedHoldings"
          />

          <!-- Returns -->
          <NewReturnsArea
            v-if="checkupStore.areas.some(area => area.name === 'Returns')"
            :class="{ 'tw-hidden': checkupStore.activeArea !== 'Returns' }"
            :disabled="checkupStore.noMatchedHoldings"
          />

          <!-- Trailing Volatility -->
          <VolatilityArea
            v-if="checkupStore.areas.some(area => area.name === 'Trailing Volatility')"
            :class="{ 'tw-hidden': checkupStore.activeArea !== 'Trailing Volatility' }"
            :disabled="checkupStore.noMatchedHoldings"
          />

          <!-- Risk/Return -->
          <NewVolatilityAndReturnsArea
            v-if="checkupStore.areas.some(area => area.name === 'Risk/Return')"
            :class="{ 'tw-hidden': checkupStore.activeArea !== 'Risk/Return' }"
            :disabled="checkupStore.noMatchedHoldings"
          />

          <!-- Market Cap Breakdown -->
          <MarketCapArea
            v-if="checkupStore.areas.some(area => area.name === 'Market Cap Breakdown')"
            :class="{ 'tw-hidden': checkupStore.activeArea !== 'Market Cap Breakdown' }"
            :disabled="checkupStore.noMatchedHoldings"
          />

          <!-- Style Category Breakdown -->
          <StyleCategoryArea
            v-if="checkupStore.areas.some(area => area.name === 'Style Category Breakdown')"
            :class="{ 'tw-hidden': checkupStore.activeArea !== 'Style Category Breakdown' }"
            :disabled="checkupStore.noMatchedHoldings"
          />

          <!-- Equity Sector Breakdown -->
          <SectorArea
            v-if="checkupStore.areas.some(area => area.name === 'Equity Sector Breakdown')"
            :class="{ 'tw-hidden': checkupStore.activeArea !== 'Equity Sector Breakdown' }"
            :disabled="checkupStore.noMatchedHoldings"
          />

          <!-- Equity Geography Breakdown -->
          <GeographyArea
            v-if="checkupStore.areas.some(area => area.name === 'Equity Geography Breakdown')"
            :class="{ 'tw-hidden': checkupStore.activeArea !== 'Equity Geography Breakdown' }"
            :disabled="checkupStore.noMatchedHoldings"
          />

          <!-- Concentration Breakdown -->
          <HoldingsConcentrationArea
            v-if="checkupStore.areas.some(area => area.name === 'Concentration Breakdown')"
            :class="{ 'tw-hidden': checkupStore.activeArea !== 'Concentration Breakdown' }"
            :disabled="checkupStore.noMatchedHoldings"
          />

          <!-- Fixed Income Type Breakdown -->
          <FixedIncomeTypeArea
            v-if="checkupStore.areas.some(area => area.name === 'Fixed Income Type Breakdown')"
            :class="{ 'tw-hidden': checkupStore.activeArea !== 'Fixed Income Type Breakdown' }"
            :disabled="checkupStore.noMatchedHoldings"
          />

          <!-- Fixed Income Maturity Breakdown -->
          <FixedIncomeDurationArea
            v-if="checkupStore.areas.some(area => area.name === 'Fixed Income Maturity Breakdown')"
            :class="{ 'tw-hidden': checkupStore.activeArea !== 'Fixed Income Maturity Breakdown' }"
            :disabled="checkupStore.noMatchedHoldings"
          />

          <!-- Fees -->
          <FeesArea
            v-if="checkupStore.areas.some(area => area.name === 'Fees')"
            :class="{ 'tw-hidden': checkupStore.activeArea !== 'Fees' }"
            :disabled="checkupStore.noMatchedHoldings"
          />

          <StatisticsArea
            v-if="checkupStore.areas.some(area => area.name === 'Statistics')"
            :class="{ 'tw-hidden': checkupStore.activeArea !== 'Statistics' }"
            :disabled="checkupStore.noMatchedHoldings"
          />

        <!-- Asset Location -->
        <AssetLocationArea
          v-if="checkupStore.areas.some(area => area.name === 'Asset Location')"
          :class="{ 'tw-hidden': checkupStore.activeArea !== 'Asset Location' }"
          :disabled="checkupStore.noMatchedHoldings"
        />

        <div v-if="checkupStore.activeArea === 'Optimization'"
          class="tw-w-full tw-flex tw-justify-center">
          <!-- Optimization Error Notification -->
          <div v-if="checkupStore.optimizationError && !hasOptimizations" class="tw-max-w-5xl tw-w-full tw-px-12 tw-pt-12">
            <b-notification
              type="is-danger"
            >
              <div class="tw-flex tw-items-center tw-gap-5">
                <i class="fa fa-times-circle tw-text-2xl" />
                <div class="tw-w-full">
                  <p class="tw-font-bold tw-mb-3">
                    Optimization Failed
                  </p>
                  <p v-if="showDetailedError" class="tw-mb-3 tw-whitespace-pre-line">
                    {{ optimizationErrorMessage }}
                  </p>
                  <p v-else class="tw-mb-3">
                    The optimization could not be completed with the current constraints. Please try adjusting your portfolio preferences and try again.
                  </p>
                  <button
                    class="button"
                    @click="checkupStore.optimizationError = null"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </b-notification>
          </div>

          <OptimizerArea v-if="hasOptimizations" />
        </div>
        <div v-if="hasAnalysisAreas && checkupStore.activeArea === 'Export'" :disabled="checkupStore.noMatchedHoldings">
          <ExportArea :areas="checkupStore.areas" />
          <CheckupDisclaimer />
        </div>
      </div>
      <!-- <footer class="pro-blue sticky-bottom">
        <div class="columns is-vcentered p-2">
          <div class="column is-one-third">
            <span class="icon has-text-primary"><i class="fas fa-arrow-left"></i></span>
            <a class="has-text-white">Previous Section</a>
          </div>
          <div class="column is-one-third has-text-centered">


            <div class="buttons">
              <a class="button is-medium">
                <span>Analysis</span>
                <span class="icon"><i class="fas fa-chart-pie fa-md"></i></span>
              </a>
              <a class="button is-medium is-inverted">
                <span>Presentation</span>
                <span class="icon"><i class="fas fa-tv fa-md"></i></span>
              </a>
            </div>
          </div>
          <div class="column is-one-third  has-text-centered">
            <a class="has-text-white">Next Section</a>
            <span class="icon has-text-primary"><i class="fas fa-arrow-right"></i></span>
          </div>
        </div>

       </footer>  -->
    </div>


    <!-- <div class='tw-bg-[#252f4a] tw-py-10'> -->
    <!-- <CheckupDisclaimer /> -->
    <!-- top level view -->
    <!-- collapseable section (steps)-->
    <!-- step 1: portfolio upload component-->
    <!-- step 2: anaylsis layout component -->
    <!-- analysis section header -->
    <!-- analysis section body -->
    <!-- analysis  3-column + 1 layout -->
    <!-- explanation  -->
    <!-- graphs  -->
    <!-- chat  -->
    <!-- preview presentation -->
    <!-- step 3: proposal presentation -->

    <!-- </div> -->

    <!-- This is a workaround to be able to export charts to pptx -->
    <!-- Charts need to be rendered, but that doesn't happen until each tab is opened. -->
    <div v-for="area in areasToCreateImages" :key="area.name" style="width: 960px">
      <ChartWrapper v-for="(chart, i) in area.chartData" :key="i" :name="`${area.name}-${i}}`" :chart-config="chart"
        :canvas-class="area.chartCanvasClasses || ''" font-size="18" invisible export-image
        @imageChanged="(data) => checkupStore.updateChart(data, area.name)"
        @imageRemoved="(id) => checkupStore.removeChart(id, area.name)" />
    </div>

    <div v-if="riskArea" style="width: 960px">
      <CheckupRiskGraphic :name="riskArea.name" :chart-config="riskArea.chartData[0]" invisible export-image
        @image-changed="(data) => checkupStore.updateSlideImage(data, riskArea.name)"
        @image-removed="(data) => checkupStore.removeSlideImage(data)" />
    </div>
  </div>
  </div>
</template>


<script>
import AdvisorCoreNav from '@frontend/ui/features/checkup/components/nav/AdvisorCoreNav.vue';
import OverviewArea from '@frontend/ui/features/checkup/components/OverviewArea.vue';
import PortfolioHoldingsAreaContainer from '@frontend/ui/features/checkup/components/PortfolioHoldingsAreaContainer.vue';
import ExportArea from '@frontend/ui/features/checkup/components/ExportArea.vue';
import PortfolioUploadSection from '@frontend/ui/features/checkup/components/PortfolioUploadSection.vue';
import AccountOnboardingSection from '@frontend/ui/features/checkup/components/AccountOnboardingSection.vue';
import DocumentDataExtractionSection from '@frontend/ui/features/checkup/components/DocumentDataExtractionSection.vue';
import SuggestedTradeArea from '@frontend/ui/features/checkup/components/SuggestedTradeArea.vue';
import ClientRiskDropdown from '@frontend/ui/features/checkup/components/ClientRiskDropdown.vue';
import TrialPayment from '@frontend/ui/components/utils/TrialPayment.vue';
import OptimizerArea from '@frontend/ui/features/checkup/components/OptimizerArea.vue';

import RiskArea from '@frontend/ui/features/checkup/components/analytics/RiskArea.vue';
import ValuesArea from '@frontend/ui/features/checkup/components/analytics/ValuesArea.vue';
import AssetClassArea from '@frontend/ui/features/checkup/components/analytics/AssetClassArea.vue';
import StyleCategoryArea from '@frontend/ui/features/checkup/components/analytics/StyleCategoryArea.vue';
import NewReturnsArea from '@frontend/ui/features/checkup/components/analytics/NewReturnsArea.vue';
import VolatilityArea from '@frontend/ui/features/checkup/components/analytics/VolatilityArea.vue';
import NewVolatilityAndReturnsArea from '@frontend/ui/features/checkup/components/analytics/NewVolatilityAndReturnsArea.vue';
import MarketCapArea from '@frontend/ui/features/checkup/components/analytics/MarketCapArea.vue';
import SectorArea from '@frontend/ui/features/checkup/components/analytics/SectorArea.vue';
import GeographyArea from '@frontend/ui/features/checkup/components/analytics/GeographyArea.vue';
import HoldingsConcentrationArea from '@frontend/ui/features/checkup/components/analytics/HoldingsConcentrationArea.vue';
import FixedIncomeTypeArea from '@frontend/ui/features/checkup/components/analytics/FixedIncomeTypeArea.vue';
import FixedIncomeDurationArea from '@frontend/ui/features/checkup/components/analytics/FixedIncomeDurationArea.vue';
import FeesArea from '@frontend/ui/features/checkup/components/analytics/FeesArea.vue';
import RunAnalysisButton from '@frontend/ui/features/checkup/components/portfolio-select/RunAnalysisButton.vue';
import AdvisorCoreInstructions from '@frontend/ui/features/checkup/components/portfolio-select/AdvisorCoreInstructions.vue';
import AdvisorCoreFAQ from '@frontend/ui/features/checkup/components/AdvisorCoreFAQ.vue';

import StressTestArea from '@frontend/ui/features/checkup/components/analytics/stress_test/StressTestArea.vue';
import TaxTransitionArea from '@frontend/ui/features/tax/components/TaxTransitionArea.vue';
import ExecutiveSummaryArea from '@frontend/ui/features/checkup/components/ExecutiveSummaryArea.vue';
import AssetLocationArea from '@frontend/ui/features/checkup/components/AssetLocationArea.vue';
import AssetLocationAdvancedArea from '@frontend/ui/features/checkup/components/AssetLocationAdvancedArea.vue';
import StatisticsArea from '@frontend/ui/features/checkup/components/analytics/StatisticsArea.vue';
import { PageHeading } from '@stakeui';

import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { useChatStore } from '@frontend/ui/stores/chatStore';

import {
  getExpenseRatio,
  getSectorBreakdown,
  getAssetClassBreakdown,
  getAssetLocationBreakdown,
  getReturnsBreakdown,
  getVolatilityBreakdown,
  getMarketCapBreakdown,
  getStyleCategoryBreakdown,
  getExtendedStyleBreakdown,
  getRiskAnalytics,
  getValuesAlignments,
  getConcentrationsBreakdown,
  getFixedIncomeTypeBreakdown,
  getFixedIncomeDurationBreakdown,
  GRADES,
  GRADE_COLORS,
  PORTFOLIO_COLORS,
  getGeoBreakdown,
  getNewReturnsBreakdown,
  getVolatilityAndReturnsBreakdown,
} from '@frontend/ui/features/checkup/helpers/checkupFunctions.js';

import CheckupDisclaimer from '@frontend/ui/features/checkup/components/CheckupDisclaimer.vue';
import telemetry from '@frontend/api/Telemetry.js';
import SettingsArea from '@frontend/ui/features/checkup/components/SettingsArea.vue';
import ChartWrapper from '../../../components/charts/ChartWrapper.vue';
import CheckupRiskGraphic from '@frontend/ui/features/checkup/components/CheckupRiskGraphic.vue';
import OrionTaxLots from '../../../../dash3/js/components/integrations/orion/OrionTaxLots.vue';


export default {
  name: 'ComponentName',
  components: {
    PortfolioUploadSection,
    AccountOnboardingSection,
    DocumentDataExtractionSection,
    OverviewArea,
    PortfolioHoldingsAreaContainer,
    CheckupDisclaimer,
    ChartWrapper,
    CheckupRiskGraphic,
    ExportArea,
    SuggestedTradeArea,
    ClientRiskDropdown,
    TrialPayment,
    OrionTaxLots,
    NewReturnsArea,
    NewVolatilityAndReturnsArea,
    RiskArea,
    ValuesArea,
    AssetClassArea,
    StyleCategoryArea,
    VolatilityArea,
    MarketCapArea,
    SectorArea,
    GeographyArea,
    HoldingsConcentrationArea,
    FixedIncomeTypeArea,
    FixedIncomeDurationArea,
    FeesArea,
    OptimizerArea,
    RunAnalysisButton,
    AdvisorCoreInstructions,
    AdvisorCoreFAQ,
    SettingsArea,
    AdvisorCoreNav,
    StressTestArea,
    TaxTransitionArea,
    ExecutiveSummaryArea,
    AssetLocationArea,
    AssetLocationAdvancedArea,
    StatisticsArea,
    PageHeading,
  },
  beforeRouteLeave(to, from, next) {
    this.checkupStore.resetState();
    next(); // Continue to desired route
  },
  props: {
    features: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isOpen: null, // For the Support Accordion
      isFAQOpen: false,
      GRADES,
      GRADE_COLORS,
      PORTFOLIO_COLORS,
      activeTab: null,
      optimizationIsRunning: false,
      announcement: null,
      filteredInclusionSymbols: [],
      filteredExclusionSymbols: [],
      filteredDoNotBuySellSymbols: [],
      currentAccountIndex: 0,
      totalAccounts: 0,
      exclusionsLoading: false,
    };
  },

  computed: {
    overlappingTickers() {
      return this.checkupStore.specificInclusions.filter(ticker =>
        this.checkupStore.specificExclusions.includes(ticker));
    },
    showOverlapWarning() {
      return this.overlappingTickers.length > 0;
    },
    isCreditBasedFirm() {
      return this.advisorStore.advisor?.firm?.credit_based_firm || false;
    },
    showSettingsTab() {
      // We now show this to all users, so that any advisor can set custon holdings output
      // Within SettingsArea.vue, only those advisors that canEditFirmSettings can see the firm level settings
      return true
      //return this.advisorStore.canEditFirmSettings;
    },
    hasAnalysisAreas() {
      return this.checkupStore.areas?.length > 1; // Should this be zero?
    },
    hasOptimizations() {
      return this.checkupStore.optimization?.optimizations?.length > 0;
    },
    hasHoldings() {
      return this.checkupStore.holdingsFromPortfolios?.length > 0 || this.checkupStore.benchmark || this.checkupStore.optimizedPortfolio;
    },
    hasOtherTabs() {
      return this.showSettingsTab || this.hasHoldings || this.hasAnalysisAreas || this.hasOptimizations;
    },
    areasToCreateImages() {
      return this.checkupStore.areas.filter(a => !['Risk', 'Returns', 'Risk/Return', 'Asset Class Breakdown', 'Style Category Breakdown'].includes(a.name));
    },
    riskArea() {
      return this.checkupStore.areas?.find(a => a.name === 'Risk');
    },
    ...mapStores(useCheckupStore, useAdvisorStore, useChatStore),
    showDetailedError() {
      // Show detailed error if the "Feature: Portfolio Checkup Optimization Tax" feature flag is enabled
      return this.checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization Tax');
    },
    optimizationErrorMessage() {
      if (!this.checkupStore.optimizationError) return '';

      const error = this.checkupStore.optimizationError;
      const errorMsg = error?.task?.meta?.error_message || '';

      // If there are violated constraints, format them nicely
      if (error?.task?.meta?.diagnostics?.violated_constraints?.length > 0) {
        const constraints = error.task.meta.diagnostics.violated_constraints;

        // Make constraint names more readable
        const readableConstraints = constraints.map(c => {
          return c
            .replace(/constraint_/g, '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
        });

        return `Optimization failed due to conflicting constraints:\n\n${readableConstraints.map(c => `• ${c}`).join('\n')}\n\n${errorMsg}`;
      }

      return errorMsg || 'An unknown error occurred during optimization.';
    },
    // totalSavings() {
    //   return (this.checkupStore.areas.reduce((acc, area) => !isNaN(area.cumulative_fee_difference) ? acc + area.cumulative_fee_difference : acc, 0)).toFixed(2);
    // },
    // savingsPercentage() {
    //   return (this.totalSavings / this.checkupStore.portfolio.total_dollars * 100).toFixed(2);
    // },
  },
  watch: {
    activeTab() {
      const i = this.activeTab - 3;
      this.chatStore.activeTabName = i < 0 ? '' : this.checkupStore.areas[i]?.name;
      console.log('activeTabName', this.chatStore.activeTabName);
    },
    async features() {
      if (!Object.keys(this.features)?.length) return;

      if (!this.features['Portfolio Checkup']) {
        try {
          await fetch('/api/v1/email/clicked-disabled-advisor-core/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.$root.$data.csrfToken,
            },
          });
        } catch (error) {
          console.error('Error sending notification:', error);
        }
        this.$router.push({ name: 'dont-have-feature' });
      }
    },
  },
  beforeCreate() {},
  async created() {
    this.checkupStore.saveCSRF(this.$root.$data.csrfToken);
    const bootstrapData = JSON.parse(document.getElementById('JS_BOOTSTRAP').textContent);
    this.$root.$data.STATIC_ROOT = bootstrapData.STATIC_ROOT;
    this.checkupStore.setFeatureFlags(bootstrapData.feature_flags);
    try {
      const res = await fetch('/api/v1/get-checkup-announcement/');
      const data = await res.json();
      this.announcement = data.announcement;
    } catch (e) {
      console.error(e);
    }

    if (!this.checkupStore.featureFlags.includes('Feature: Portfolio Checkup')) {
      return this.$router.push({ name: 'home' });
    }

    if (this.checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization')) {
      console.log('GETTING SYMBOLS');
      await this.checkupStore.getSymbols();
    }
    await this.chatStore.createSocket();
    try {
      telemetry.track('Advisor Core Loaded', { 'Advisor Core Loaded': 'yes' });
    } catch (err) {
      console.error(err);
    }
  },
  mounted() {
    // this.createExpenseRatioArea();
  },
  updated() {},
  methods: {
    addOverride() {
      this.checkupStore.overrides.push({ symbol: '', minWeight: null, maxWeight: null });
    },
    removeOverride(index) {
      this.checkupStore.overrides.splice(index, 1);
    },
    validateOverrides() {
      return ! this.checkupStore.overrides.some(override => {
        return (override.symbol == '' || override.symbol == null || ! Number.isFinite(override.minWeight) || ! Number.isFinite(override.maxWeight) );
      });
    },
    async setOrionPortfolio(portfolio) {
      this.checkupStore.specificDoNotBuy = portfolio.exclusions;
      this.checkupStore.specificDoNotSell = portfolio.exclusions;
      this.checkupStore.orionPortfolioId = portfolio.portfolioId;
      this.exclusionsLoading = false;
    },
    async setExclusionsLoading() {
      this.exclusionsLoading = true;
    },
    handleTabChange(areaName) {
      const index = this.checkupStore.areas.findIndex(area => area.name === areaName);
      this.activeTab = index + 3;
      window.scrollTo(0, 0);
    },
    handleRunAnalysisNow() {
      // Call the submit method on the RunAnalysisButton component
      if (this.$refs.runAnalysisButton) {
        this.$refs.runAnalysisButton.submit();
      }
    },
    async onSelectComplete() {
      console.log('onSelectComplete STARTING');
      if (this.checkupStore.portfolio?.isChecked || this.checkupStore.benchmark?.isChecked || this.checkupStore.optimizedPortfolio?.isChecked) {
        // NOTE should indent the below, but trying to have no diff for easier review
        this.checkupStore.setAreas([]);

        let portfolio = null;
        if (this.checkupStore.portfolio?.isChecked) {
          portfolio = this.checkupStore.portfolio;
        }
        console.log('this.checkupStore.portfolio', this.checkupStore.portfolio);

        let benchmark = null;
        if (this.checkupStore.benchmark?.isChecked) {
          benchmark = this.checkupStore.benchmark;
        }
        console.log('this.checkupStore.benchmark', this.checkupStore.benchmark);

        let optimizedPortfolio = null;
        if (this.checkupStore.optimizedPortfolio?.isChecked) {
          optimizedPortfolio = this.checkupStore.optimizedPortfolio;
        }
        setTimeout(() => {
          this.createRiskArea(portfolio, benchmark, optimizedPortfolio);
          this.createValuesArea(portfolio, benchmark, optimizedPortfolio);
          this.createAssetClassBreakdownArea(portfolio, benchmark, optimizedPortfolio);
          this.createReturnsArea(portfolio, benchmark, optimizedPortfolio);
          this.createNewReturnsArea(portfolio, benchmark, optimizedPortfolio);
          
          this.createVolatilityArea(portfolio, benchmark, optimizedPortfolio);
          this.createVolatilityAndReturnsArea(portfolio, benchmark, optimizedPortfolio);
          this.createMarketCapBreakdownArea(portfolio, benchmark, optimizedPortfolio);
          this.createStyleCategoryBreakdownArea(portfolio, benchmark, optimizedPortfolio);
          // this.createExtendedStyleBreakdownArea(portfolio, benchmark, optimizedPortfolio);
          this.createSectorBreakdownArea(portfolio, benchmark, optimizedPortfolio);
          this.createGeographyBreakdownArea(portfolio, benchmark, optimizedPortfolio);
          this.createConcentrationsArea(portfolio, benchmark, optimizedPortfolio);
          this.createFixedIncomeTypeBreakdownArea(portfolio, benchmark, optimizedPortfolio);
          this.createFixedIncomeDurationBreakdownArea(portfolio, benchmark, optimizedPortfolio);
          this.createExpenseRatioArea(portfolio, benchmark, optimizedPortfolio);
          this.createStatisticsArea(portfolio, benchmark, optimizedPortfolio);
          if (this.checkupStore.portfolio && this.checkupStore.portfolio.uploaded_df_agg?.length && this.checkupStore.portfolio.uploaded_df_agg.some(item => ['Taxable', 'Tax Deferred', 'Tax Free'].includes(item.account_tax_status))) {
            this.createAssetLocationBreakdownArea(portfolio, benchmark, optimizedPortfolio);
          }
        }, 0);

        // NOTE: If you add an area, remember to add a methodology import in CheckupMethodologyWrapper
        
        if (!this.checkupStore.noMatchedHoldings) {
          setTimeout(() => {
            this.checkupStore.updateAreasWithExplanation();
          }, 0);
        }

        // Trigger stress test analysis if stress test area exists
        if (this.checkupStore.featureFlags.includes('Feature: Stress Test') && this.$refs.stressTestArea && this.$refs.stressTestArea.$refs.stressTestFeature) {
          this.$refs.stressTestArea.$refs.stressTestFeature.runAll();
        }
        
      }
      this.checkupStore.setLoadingState('isAreasLoading', false);
    },

    updateSelectedValues(values) {
      this.checkupStore.setSelectedValues(values);
    },
    createVolatilityAndReturnsArea(portfolio, benchmark, optimizedPortfolio) {
      const { chartData: newChartData, ...newRest } = getVolatilityAndReturnsBreakdown(portfolio, benchmark, optimizedPortfolio, this.advisorStore);
      // Function to darken a color
      const darkenColor = (color, percent) => {
        const num = parseInt(color.replace('#', ''), 16);
        const amt = Math.round(2.55 * percent);
        const R = (num >> 16) - amt;
        const G = (num >> 8 & 0x00FF) - amt;
        const B = (num & 0x0000FF) - amt;
        return '#' + (0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
                (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
                (B < 255 ? (B < 1 ? 0 : B) : 255)).toString(16).slice(1);
      };

      // Get the darker version of the portfolio color
      const portfolioDarkColor = darkenColor(this.advisorStore.firmAdvisorCoreSettings.portfolio_color, 20);

      const series = [];

      if (this.checkupStore.portfolio && newChartData.portfolioData) {
        series.push({
          name: `${this.checkupStore.portfolio.name} holdings`,
          color: this.advisorStore.firmAdvisorCoreSettings.portfolio_color,
          data: newChartData.portfolioData.map(point => ({
            ...point,
            marker: {
              symbol: 'circle',
              radius: Math.max(2, Math.sqrt(point.percentageOfPortfolio) * 2), // Adjust the radius based on the square root of the percentage
            },
          })),
          marker: {
            symbol: 'circle',
          },
          tooltip: {
            pointFormat: '<span style="color:{point.color}">\u25CF</span> {point.name}: <b>Volatility: {point.x:.2f}%, Return: {point.y:.2f}%</b><br/>Percentage of Portfolio: <b>{point.percentageOfPortfolio:.2f}%</b><br/>',
          },
        });

        if (newChartData.portfolioTotal) {
          series.push({
            name: this.checkupStore.portfolio.name,
            color: portfolioDarkColor,
            data: newChartData.portfolioTotal,
            marker: {
              symbol: 'diamond',
              radius: Math.sqrt(100) * 2,
            },
            tooltip: {
              pointFormat: '<span style="color:{point.color}">\u25CF</span> {point.name}: <b>Volatility: {point.x:.2f}%, Return: {point.y:.2f}%</b><br/>',
            },
          });
        }
      }

      if (this.checkupStore.benchmark && newChartData.benchmarkData) {
        series.push({
          name: `${this.checkupStore.benchmark.name} holdings`,
          color: this.advisorStore.firmAdvisorCoreSettings.benchmark_color,
          visible: false,
          data: newChartData.benchmarkData.map(point => ({
            ...point,
            marker: {
              symbol: 'circle',
              radius: Math.max(2, Math.sqrt(point.percentageOfPortfolio) * 2), // Adjust the radius based on the square root of the percentage
            },
          })),
          marker: {
            symbol: 'circle',
          },
          tooltip: {
            pointFormat: '<span style="color:{point.color}">\u25CF</span> {point.name}: <b>Volatility: {point.x:.2f}%, Return: {point.y:.2f}%</b><br/>Percentage of Portfolio: <b>{point.percentageOfPortfolio:.2f}%</b><br/>',
          },
        });

        if (newChartData.benchmarkTotal) {
          series.push({
            name: this.checkupStore.benchmark.name,
            color: this.advisorStore.firmAdvisorCoreSettings.benchmark_color,
            data: newChartData.benchmarkTotal,
            marker: {
              symbol: 'diamond',
              radius: Math.sqrt(100) * 2,
            },
            tooltip: {
              pointFormat: '<span style="color:{point.color}">\u25CF</span> {point.name}: <b>Volatility: {point.x:.2f}%, Return: {point.y:.2f}%</b><br/>',
            },
          });
        }
      }

      if (this.checkupStore.optimizedPortfolio && newChartData.optimizedData) {
        series.push({
          name: `${this.checkupStore.optimizedPortfolio.name} holdings`,
          color: this.advisorStore.firmAdvisorCoreSettings.proposed_color,
          data: newChartData.optimizedData.map(point => ({
            ...point,
            marker: {
              symbol: 'circle',
              radius: Math.max(2, Math.sqrt(point.percentageOfPortfolio) * 2),
            },
          })),
          marker: {
            symbol: 'circle',
          },
          tooltip: {
            pointFormat: '<span style="color:{point.color}">\u25CF</span> {point.name}: <b>Volatility: {point.x:.2f}%, Return: {point.y:.2f}%</b><br/>Percentage of Portfolio: <b>{point.percentageOfPortfolio:.2f}%</b><br/>',
          },
        });

        if (newChartData.optimizedTotal) {
          series.push({
            name: this.checkupStore.optimizedPortfolio.name,
            color: this.advisorStore.firmAdvisorCoreSettings.proposed_color,
            data: newChartData.optimizedTotal,
            marker: {
              symbol: 'diamond',
              radius: Math.sqrt(100) * 2,
            },
            tooltip: {
              pointFormat: '<span style="color:{point.color}">\u25CF</span> {point.name}: <b>Volatility: {point.x:.2f}%, Return: {point.y:.2f}%</b><br/>',
            },
          });
        }
      }

      this.checkupStore.pushToAreas({
        name: 'Risk/Return',
        ...newRest,
        chartData: [{
          library: 'highcharts',
          chart: {
            type: 'scatter',
          },
          title: {
            text: 'Risk/Return',
          },
          xAxis: {
            title: {
              text: '3 Year Volatility (Std Dev)',
            },
            labels: {
              formatter: function() {
                return this.value + '%';
              },
            },
          },
          yAxis: {
            title: {
              text: '3 Year Returns (Annualized)',
            },
            opposite: false,
            labels: {
              formatter: function() {
                return this.value + '%';
              },
            },
          },
          series: series,
        }],
        headerText: `Plotting volatility versus returns on a scatter plot visualizes the relationship between risk and reward in investing. Each dot represents the historical returns and volatility of a holding or a portfolio, and demonstrates the general correlation between higher returns and higher volatility. Investors generally try to maximize returns while minimizing volatility. Portfolios and holdings with higher returns and lower volatility are displayed towards the upper left of the chart.`,
      });
      console.log('Risk/Return Area:', this.checkupStore.areas.find(area => area.name === 'Risk/Return'));
    },
    createGeographyBreakdownArea(portfolio, benchmark, optimizedPortfolio) {
      const { chartData, ...rest } = getGeoBreakdown(portfolio, benchmark, optimizedPortfolio, this.advisorStore);
      this.checkupStore.pushToAreas({
        name: 'Equity Geography Breakdown',
        ...rest,
        clickthroughFunction: this.checkupStore.getGeographyLookthrough,
        chartData: [{
          ...chartData,
          type: 'bar',
          options: {
            scales: {
              y: {
                title: { display: true, text: '% of Portfolio Allocation' },
              },
            },
          },
          formats: { y: 'percent' },
        }],
        headerText: `Geography in investing refers to the allocation of investments across global regions, commonly segmented into the United States, Developed Markets (such as Europe and Japan), and Emerging Markets (such as India and Brazil). Companies within a geographic region generally exhibit higher correlations in their returns because they are influenced by the political and economic conditions of their regions. For example, developed markets like the United States and Europe tend to experience different economic cycles compared to emerging markets like India and Brazil. Diversifying investments across multiple geographies can mitigate risks associated with regional volatilities and economic downturns.`,
      });
    },
    createConcentrationsArea(portfolio, benchmark, optimizedPortfolio) {
      const {
        chartDataForPortfolio,
        chartDataForBenchmark,
        chartDataForOptimized,
        ...rest
      } = getConcentrationsBreakdown(portfolio, benchmark, optimizedPortfolio, this.advisorStore);

      const chartData = [];

      if (chartDataForPortfolio && this.checkupStore.portfolio) {
        chartData.push({
          ...chartDataForPortfolio,
          type: 'bar',
          options: {
            plugins: {
              legend: {
                display: false,
              },
              title: {
                display: true,
                text: this.checkupStore.portfolio.name,
              },
            },
            scales: {
              x: {
                ticks: {
                  autoSkip: false,
                },
              },
              y: {
                title: { display: true, text: 'Percent of portfolio' },
              },
            },
          },
          formats: { y: 'percent', x: 'labels' },
        });
      }

      if (chartDataForBenchmark && this.checkupStore.benchmark) {
        chartData.push({
          ...chartDataForBenchmark,
          type: 'bar',
          options: {
            plugins: {
              legend: {
                display: false,
              },
              title: {
                display: true,
                text: this.checkupStore.benchmark.name,
              },
            },
            scales: {
              y: {
                title: { display: true, text: 'Percent of portfolio' },
              },
            },
          },
          formats: { y: 'percent', x: 'labels' },
        });
      }

      if (chartDataForOptimized && this.checkupStore.optimizedPortfolio) {
        chartData.push({
          ...chartDataForOptimized,
          type: 'bar',
          options: {
            plugins: {
              legend: {
                display: false,
              },
              title: {
                display: true,
                text: this.checkupStore.optimizedPortfolio.name,
              },
            },
            scales: {
              y: {
                title: { display: true, text: 'Percent of portfolio' },
              },
            },
          },
          formats: { y: 'percent', x: 'labels' },
        });
      }

      this.checkupStore.pushToAreas({
        name: 'Concentration Breakdown',
        ...rest,
        chartCanvasClasses: 'tw-max-h-60',
        clickthroughFunction: this.checkupStore.getConcentrationLookthrough,
        chartData,
        topRightComponent: {
          component: 'ConcentrationHelperText',
        },
        headerText: `Holdings concentration in investments refers to how much of your portfolio is invested in specific assets. It looks at the percentage of your portfolio allocated to certain stocks, bonds, or other types of investments. Concentration risk is often compared to a benchmark and different levels of concentration may cause returns to vary. For example, if a portfolio has a large  position in one stock compared to a benchmark, its returns might be more volatile relative to the benchmark. While a concentrated portfolio can be riskier if a single asset performs poorly, it also has the potential for higher returns if that asset does well. On the other hand, spreading investments across a wider range of assets can help reduce the risk tied to any single investment.`,
      });
    },
    createRiskArea(portfolio, benchmark, optimizedPortfolio) {
      const areaData = getRiskAnalytics(portfolio, benchmark, optimizedPortfolio, this.advisorStore);
      this.checkupStore.pushToAreas({
        name: 'Risk',
        ...areaData,
        clickthroughFunction: this.checkupStore.getRiskLookthrough,
        headerText: `Risk level in investment portfolios refers to the degree of uncertainty and potential financial loss associated with the investments held within the portfolio. Higher risk comes with higher potential rewards and higher potential losses. Risk levels can also be applied to individuals based on their risk tolerance—how much risk they are comfortable taking—and risk capacity—how much risk they can afford to take. A suitable portfolio for you is one whose risk level aligns with your risk profile, such that potential returns are weighed against your ability and willingness to absorb risk.`,
      });
    },
    createValuesArea(portfolio, benchmark, optimizedPortfolio) {
      const { chartData, ...rest } = getValuesAlignments(portfolio, benchmark, optimizedPortfolio, this.advisorStore);
      console.log('VALUES CHART DATA', chartData);

      const formattedChartData = {
        ...chartData,
        datasets: chartData.datasets.filter(dataset => dataset.data.length > 0),
      };

      this.checkupStore.pushToAreas({
        name: 'Values Alignment',
        ...rest,
        chartData: formattedChartData.datasets.length > 0 ? [{
          ...formattedChartData,
          type: 'bar',
          options: {
            scales: {
              y: {
                title: { display: true, text: 'Alignment Score' },
              },
            },
          },
          formats: { y: 'percent' },
        }] : [],
        topRightComponent: {
          component: 'PortfolioComparisonLink',
          props: {
            portfolioExtID: this.checkupStore.optimization && this.checkupStore.portfolio?.equity_only_id && this.checkupStore.benchmark?.equity_only_id ? this.checkupStore.portfolio?.equity_only_id : this.checkupStore.portfolio?.external_id,
            benchmarkExtID: this.checkupStore.optimization && this.checkupStore.benchmark?.equity_only_id ? this.checkupStore.benchmark?.equity_only_id : this.checkupStore.benchmark?.external_id,
            screensetIDs: this.checkupStore.selectedValueIds || [],
          },
        },
        headerText: `Values analysis involves assessing how well your investments align with the issues that matter to you. The business practices and social/environmental impacts (e.g. forced labor involvement, toxic pollution, etc.) of the companies in a portfolio are determined through the collection of publicly available data from government, academic, regulatory, and nonprofit sources. Company performance on these data points is then mapped to the relevant values to determine an alignment level. Alignment levels are normalized such that a 50% alignment is the median for all investments.`,
      });
    },
    createExpenseRatioArea(portfolio, benchmark, optimizedPortfolio) {
      const { chartData, ...rest } = getExpenseRatio(portfolio, benchmark, optimizedPortfolio, this.checkupStore.fees.portfolio, this.checkupStore.fees.benchmark, this.checkupStore.fees.optimized, this.advisorStore);
      console.log('CHART DATA EXPENSE RATIO', chartData);
      // Filter out any null datasets and ensure data exists
      const filteredChartData = {
        ...chartData,
        datasets: (chartData.datasets || []).filter(dataset => dataset && dataset.data && dataset.data.length > 0),
      };

      const extraData = [];
      if (this.checkupStore.portfolio) {
        extraData.push(
            { name: `${this.checkupStore.portfolio.name || 'Portfolio'} Avg Expense Ratio`, value: (this.checkupStore.portfolio.average_expense_ratio * 100).toFixed(2) + '%' },
            { name: `${this.checkupStore.portfolio.name || 'Portfolio'} Advisory and Mgmt Fees`, value: (rest.portfolioAdditionalFees * 100).toFixed(2) + '%' },
        );
      }
      if (this.checkupStore.benchmark) {
        extraData.push(
            { name: `${this.checkupStore.benchmark.name || 'Benchmark'} Avg Expense Ratio`, value: (this.checkupStore.benchmark.average_expense_ratio * 100).toFixed(2) + '%' },
            { name: `${this.checkupStore.benchmark.name || 'Benchmark'} Advisory and Mgmt Fees`, value: (rest.benchmarkAdditionalFees * 100).toFixed(2) + '%' },
        );
      }
      if (this.checkupStore.optimizedPortfolio) {
        extraData.push(
            { name: `${this.checkupStore.optimizedPortfolio.name || 'Proposed'} Avg Expense Ratio`, value: (this.checkupStore.optimizedPortfolio.average_expense_ratio * 100).toFixed(2) + '%' },
            { name: `${this.checkupStore.optimizedPortfolio.name || 'Proposed'} Advisory and Mgmt Fees`, value: (rest.proposedAdditionalFees * 100).toFixed(2) + '%' },
        );
      }

      this.checkupStore.pushToAreas({
        name: 'Fees',
        ...rest,
        clickthroughFunction: this.checkupStore.getExpenseRatioLookthrough,
        chartData: filteredChartData.datasets.length > 0 ? [{
          ...filteredChartData,
          type: 'line',
          options: {
            scales: {
              x: {
                title: { display: true, text: 'Year' },
              },
              y: {
                title: { display: true, text: 'Cumulative Fees' },
              },
            },
          },
          formats: { y: 'currency' },
        }] : [],
        extraData,
        topLeftComponent: {
          component: 'AdvisorFeeInput',
          props: {},
        },
        headerText: `The Fees chart provides a visual representation of the combined impact of three types of fees you may encounter: expense ratios, management fees, and advisory fees. Expense ratios are the fees associated with the management and administration of mutual funds and ETFs. Management fees apply to the management and administration of investment portfolios. Advisory fees are charged for the various services provided by your financial advisor. These fees are typically expressed as a percentage of assets. In the chart, these fees are shown in dollar terms based on a specified portfolio value and an assumed 8% annual growth rate for illustrative purposes.`,
      });
      console.log('CREATE ExpenseRatio DATA', filteredChartData);
    },
    createSectorBreakdownArea(portfolio, benchmark, optimizedPortfolio) {
      const { chartData, ...rest } = getSectorBreakdown(portfolio, benchmark, optimizedPortfolio, this.advisorStore);
      this.checkupStore.pushToAreas({
        name: 'Equity Sector Breakdown',
        ...rest,
        clickthroughFunction: this.checkupStore.getSectorLookthrough,
        chartData: [{
          ...chartData,
          type: 'bar',
          options: {
            scales: {
              y: {
                title: { display: true, text: '% of Portfolio Allocation' },
              },
            },
          },
          formats: { y: 'percent' },
        }],
        headerText: `Sectors in investing refer to broad categories of the economy, such as Financials, Industrials, Information Technology, and Health Care. Each sector represents a group of related companies that may have higher correlations in their returns because they are influenced by similar market factors. A diversified sector allocation generally has less risk than a concentrated sector allocation. For example, when technology stocks are struggling, energy stocks might be thriving, and vice versa.`,
      });
    },
    createAssetClassBreakdownArea(portfolio, benchmark, optimizedPortfolio) {
      const { seriesDataForHighcharts, pieSize, ...rest } = getAssetClassBreakdown(portfolio, benchmark, optimizedPortfolio, this.advisorStore);
      this.checkupStore.pushToAreas({
        name: 'Asset Class Breakdown',
        ...rest,
        clickthroughFunction: this.checkupStore.getAssetClassLookthrough,
        // showChartsAsRow: true, // Not needed for highcharts
        chartData: [{
          library: 'highcharts',
          chart: {
            type: 'pie',
            spacing: [50, 0, 50, 0], // Adjust spacing between the two charts
          },
          title: {
            text: '',
          },
          plotOptions: {
            pie: {
              size: `${pieSize}%`, // Set the size of each pie chart
              dataLabels: {
                enabled: true,
                format: '{point.percentage:.1f}% {point.name}',
                style: {
                  fontSize: '12px',
                },
              },
            },
          },
          tooltip: {
            pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.percentage:.1f}%</b><br/>',
          },
          series: seriesDataForHighcharts,
        }],
        headerText: `Asset classes are categories of investments that exhibit similar characteristics, grouped into categories such as US Stocks, International Stocks, Bonds, Cash, and Other (including privately held investments). An asset class breakdown describes how a portfolio is allocated across these categories. Asset classes respond differently under various market conditions due to their inherent traits. For example, a higher allocation in stocks may be associated with a riskier portfolio which may have higher potential returns and higher potential losses. Conversely, a higher allocation in bonds may be associated with a less risky portfolio, which may have lower potential returns and lower potential losses. `,
      });
    },
    createAssetLocationBreakdownArea(portfolio, benchmark, optimizedPortfolio) {
      const { portfolioSeriesDataForHighcharts, portfolioPieSize, optimizedPortfolioSeriesDataForHighcharts, optimizedPortfolioPieSize, ...rest } = getAssetLocationBreakdown(portfolio, benchmark, optimizedPortfolio, this.advisorStore);
      this.checkupStore.pushToAreas({
        name: 'Asset Location',
        ...rest,
        clickthroughFunction: this.checkupStore.getAssetLocationLookthrough,
        // showChartsAsRow: true, // Not needed for highcharts
        portfolioChartData: [{
          library: 'highcharts',
          chart: {
            type: 'pie',
            spacing: [50, 0, 50, 0], // Adjust spacing between the two charts
          },
          title: {
            text: '',
          },
          plotOptions: {
            pie: {
              size: `${portfolioPieSize}%`, // Set the size of each pie chart
              dataLabels: {
                enabled: true,
                format: '{point.percentage:.1f}% {point.name}',
                style: {
                  fontSize: '12px',
                },
              },
            },
          },
          tooltip: {
            pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.percentage:.1f}%</b><br/>',
          },
          series: portfolioSeriesDataForHighcharts,
        }],
        optimizedPortfolioChartData: [{
          library: 'highcharts',
          chart: {
            type: 'pie',
            spacing: [50, 0, 50, 0], // Adjust spacing between the two charts
          },
          title: {
            text: '',
          },
          plotOptions: {
            pie: {
              size: `${optimizedPortfolioPieSize}%`, // Set the size of each pie chart
              dataLabels: {
                enabled: true,
                format: '{point.percentage:.1f}% {point.name}',
                style: {
                  fontSize: '12px',
                },
              },
            },
          },
          tooltip: {
            pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.percentage:.1f}%</b><br/>',
          },
          series: optimizedPortfolioSeriesDataForHighcharts,
        }],
        headerText: `Asset Location is the practice of strategically placing investments in different types of accounts (taxable, tax-deferred, and tax-free) to maximize after-tax returns. In taxable accounts, dividends, interest, and capital gains are taxed annually. In tax-deferred accounts (e.g., IRAs, 401(k)s), taxes on dividends, interest, and gains are deferred until withdrawal. In tax-free accounts (e.g., Roth IRAs), investments grow and can be withdrawn without paying taxes on gains, dividends, or interest.`,
      });
    },

    createReturnsArea(portfolio, benchmark, optimizedPortfolio) {
      const { chartData, ...rest } = getReturnsBreakdown(portfolio, benchmark, optimizedPortfolio, this.advisorStore);
      this.checkupStore.pushToAreas({
        name: 'Trailing Returns',
        ...rest,
        clickthroughFunction: this.checkupStore.getTrailingReturnsLookthrough,
        chartData: [{
          library: 'highcharts',
          chart: {
            type: 'column',
          },
          xAxis: {
            categories: ['1 Year', '3 Year', '5 Year', '10 Year'],
          },
          yAxis: {
            title: {
              text: 'Percentage Return (Annualized)',
            },
          },
          rangeSelector: {
            enabled: false,
          },
          title: {
            text: 'Trailing Returns',
          },
          series: [
            this.checkupStore.portfolio && {
              name: this.checkupStore.portfolio.name,
              type: 'column',
              color: this.advisorStore.firmAdvisorCoreSettings.portfolio_color,
              data: chartData.portfolioData || [],
              tooltip: {
                valueDecimals: 2,
              },
            },
            this.checkupStore.benchmark && {
              name: this.checkupStore.benchmark.name,
              type: 'column',
              color: this.advisorStore.firmAdvisorCoreSettings.benchmark_color,
              data: chartData.benchmarkData || [],
              tooltip: {
                valueDecimals: 2,
              },
            },
            this.checkupStore.optimizedPortfolio && {
              name: this.checkupStore.optimizedPortfolio.name,
              type: 'column',
              color: this.advisorStore.firmAdvisorCoreSettings.proposed_color,
              data: chartData.optimizedData || [],
              tooltip: {
                valueDecimals: 2,
              },
            },
          ].filter(Boolean),
        }],
      });
    },
    createNewReturnsArea(portfolio, benchmark, optimizedPortfolio) {
      const { chartData: newChartData, ...newRest } = getNewReturnsBreakdown(portfolio, benchmark, optimizedPortfolio, this.advisorStore);
      console.log('createNewReturnsArea.newChartData', newChartData);
      this.checkupStore.pushToAreas({
        name: 'Returns',
        ...newRest,
        clickthroughFunction: this.checkupStore.getTrailingReturnsLookthrough,
        chartData: [{
          library: 'highcharts',
          chart: {
            type: 'line',
          },
          legend: {
            enabled: true,
          },
          rangeSelector: {
            enabled: true,
            buttons: [{
              type: 'year',
              count: 1,
              text: '1y',
            }, {
              type: 'year',
              count: 3,
              text: '3y',
            }, {
              type: 'year',
              count: 5,
              text: '5y',
            }, {
              type: 'year',
              count: 10,
              text: '10y',
            }, {
              type: 'all',
              text: 'All',
            }],
          },
          title: {
            text: 'Growth of Hypothetical Portfolio',
          },
          xAxis: {
            type: 'datetime',
            title: {
              text: 'Date',
            },
          },
          yAxis: {
            title: {
            // text: 'Y-Axis Label',
            },
            opposite: false,
            labels: {
              formatter: function() {
                return this.value + '%';
              },
            },
          },
          series: [
            this.checkupStore.portfolio && newChartData.portfolioData && {
              name: this.checkupStore.portfolio.name,
              type: 'line',
              color: this.advisorStore.firmAdvisorCoreSettings.portfolio_color,
              dataGrouping: { units: [['year', [1]]] },
              data: newChartData.portfolioData,
              tooltip: {
                pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y:.1f}%</b><br/>',
              },
            },
            this.checkupStore.benchmark && newChartData.benchmarkData && {
              name: this.checkupStore.benchmark.name,
              type: 'line',
              color: this.advisorStore.firmAdvisorCoreSettings.benchmark_color,
              dataGrouping: { units: [['year', [1]]] },
              data: newChartData.benchmarkData,
              tooltip: {
                pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y:.1f}%</b><br/>',
              },
            },
            this.checkupStore.optimizedPortfolio && newChartData.optimizedData && {
              name: this.checkupStore.optimizedPortfolio.name,
              type: 'line',
              color: this.advisorStore.firmAdvisorCoreSettings.proposed_color,
              dataGrouping: { units: [['year', [1]]] },
              data: newChartData.optimizedData,
              tooltip: {
                pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y:.1f}%</b><br/>',
              },
            },
          ].filter(Boolean),
        }],
        headerText: `Returns measure the gain or loss of an investment portfolio over a specified period, expressed as a percentage of your initial investment. Returns are often analyzed in comparison to an appropriate benchmark. Analyzing portfolio returns over time provides valuable insights into how the portfolio has performed under different market conditions. Generally, higher returns correlate with higher volatility, and the converse is also true. Some investors might prefer more stable returns, while those comfortable with more risk may aim for higher potential returns, accepting the associated potential for higher losses. Returns are measured by examining past performance and do not indicate future results.`,
      });
      console.log('NEW RETURNS AREA', this.checkupStore.areas);

    // console.log('NEW RETURNS AREA', this.checkupStore.areasByName['Returns']);
    },
    createVolatilityArea(portfolio, benchmark, optimizedPortfolio) {
      const { chartData, ...rest } = getVolatilityBreakdown(portfolio, benchmark, optimizedPortfolio, this.advisorStore);
      this.checkupStore.pushToAreas({
        name: 'Trailing Volatility',
        ...rest,
        clickthroughFunction: this.checkupStore.getTrailingVolatilitiesLookthrough,
        chartData: [{
          ...chartData,
          type: 'bar',
          options: {
            scales: {
              y: {
                title: { display: true, text: 'Volatility Measure' },
              },
            },
          },
          formats: { y: 'percent' },
        }],
        headerText: `Volatility measures the standard deviation of a portfolio's returns, reflecting how much the returns of an investment portfolio deviate from their average. High volatility indicates significant fluctuations from this average, suggesting the potential for higher potential returns and higher potential losses. Conversely, low volatility signifies that the returns are relatively consistent with the average, indicating more stability.`,
      });
    },

    createMarketCapBreakdownArea(portfolio, benchmark, optimizedPortfolio) {
      try {
        const { chartData, ...rest } = getMarketCapBreakdown(portfolio, benchmark, optimizedPortfolio, this.advisorStore);
        this.checkupStore.pushToAreas({
          name: 'Market Cap Breakdown',
          ...rest,
          clickthroughFunction: this.checkupStore.getMarketCapLookthrough,
          chartData: [{
            ...chartData,
            type: 'bar',
            options: {
              scales: {
                y: {
                  title: { display: true, text: '% of Portfolio Allocation' },
                },
              },
            },
            formats: { y: 'percent' },
          }],
          headerText: `Market capitalization (market cap) measures a company's total value as determined by the stock market, calculated by multiplying the company's share price by its total outstanding shares. Companies are categorized into different market cap sizes: Mega Cap (over $200B), Large Cap ($10B-$200B), Mid Cap ($2B-$10B), Small Cap ($300M-$2B), and Micro Cap (under $300M). Understanding the market cap breakdown of your portfolio offers insights into the size and stability of the companies in your portfolio. Large-cap companies are generally more stable with lower potential returns and lower potential losses whereas smaller companies, like those in the small and micro-cap categories, may provide higher potential returns and higher potential losses.`,
        });
      } catch (error) {
        console.error('error', error);
      }
    },
    createStyleCategoryBreakdownArea(portfolio, benchmark, optimizedPortfolio) {
      const { seriesDataForHighcharts, pieSize, ...rest } = getStyleCategoryBreakdown(portfolio, benchmark, optimizedPortfolio, this.advisorStore);
      this.checkupStore.pushToAreas({
        name: 'Style Category Breakdown',
        ...rest,
        clickthroughFunction: this.checkupStore.getStyleCategoryLookthrough,
        // showChartsAsRow: true, // Not needed for highcharts
        chartData: [{
          library: 'highcharts',
          chart: {
            type: 'pie',
            spacing: [50, 0, 50, 0], // Adjust spacing between the two charts
          },
          title: {
            text: '',
          },
          plotOptions: {
            pie: {
              size: `${pieSize}%`, // Set the size of each pie chart
              dataLabels: {
                enabled: true,
                format: '{point.percentage:.1f}% {point.name}',
                style: {
                  fontSize: '12px',
                },
              },
            },
          },
          tooltip: {
            pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.percentage:.1f}%</b><br/>',
          },
          series: seriesDataForHighcharts,
        }],
        headerText: `Style categories classify stocks into nine categories based on their investment style (Growth, Value, and Core) and market capitalization (Large, Mid, and Small). Value stocks are priced lower than what their fundamental metrics might suggest, indicating they might be undervalued by the market. Growth stocks have higher expected growth rates in earnings, revenues, or cash flow. Core stocks exhibit similar levels of value and growth characteristics. Larger companies and value-oriented companies generally provide lower potential returns and lower potential losses whereas smaller companies and growth-oriented companies may provide higher potential returns and higher potential losses.`,
      });
    },
    createExtendedStyleBreakdownArea(portfolio, benchmark, optimizedPortfolio) {
      const { seriesDataForHighcharts, pieSize, ...rest } = getExtendedStyleBreakdown(portfolio, benchmark, optimizedPortfolio, this.advisorStore);
      this.checkupStore.pushToAreas({
        name: 'Extended Style Breakdown',
        ...rest,
        clickthroughFunction: this.checkupStore.getStyleLookthrough,
        // showChartsAsRow: true, // Not needed for highcharts
        chartData: [{
          library: 'highcharts',
          chart: {
            type: 'pie',
            spacing: [50, 0, 50, 0], // Adjust spacing between the two charts
          },
          title: {
            text: '',
          },
          plotOptions: {
            pie: {
              size: `${pieSize}%`, // Set the size of each pie chart
              dataLabels: {
                enabled: true,
                format: '{point.percentage:.1f}% {point.name}',
                style: {
                  fontSize: '12px',
                },
              },
            },
          },
          tooltip: {
            pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.percentage:.1f}%</b><br/>',
          },
          series: seriesDataForHighcharts,
        }],
        headerText: `Style categories classify stocks into nine categories based on their investment style (Growth, Value, and Core) and market capitalization (Large, Mid, and Small). Value stocks are priced lower than what their fundamental metrics might suggest, indicating they might be undervalued by the market. Growth stocks have higher expected growth rates in earnings, revenues, or cash flow. Core stocks exhibit similar levels of value and growth characteristics. Larger companies and value-oriented companies generally provide lower potential returns and lower potential losses whereas smaller companies and growth-oriented companies may provide higher potential returns and losses.`,
      });
    },
    createFixedIncomeTypeBreakdownArea(portfolio, benchmark, optimizedPortfolio) {
      try {
        const { chartData, ...rest } = getFixedIncomeTypeBreakdown(portfolio, benchmark, optimizedPortfolio, this.advisorStore);
        this.checkupStore.pushToAreas({
          name: 'Fixed Income Type Breakdown',
          ...rest,
          clickthroughFunction: this.checkupStore.getFixedIncomeTypeLookthrough,
          chartData: [{
            ...chartData,
            type: 'bar',
            options: {
              scales: {
                y: {
                  title: { display: true, text: '% of Portfolio Allocation' },
                },
              },
            },
            formats: { y: 'percent' },
          }],
          headerText: `Fixed income investments (most commonly bonds) are debt securities that provide regular interest payments and are categorized into Government, Corporate, Municipal, and Securitized types. Government bonds are issued by national governments. Corporate bonds are issued by companies to raise capital. Municipal bonds are issued by state and local governments to fund public projects and may provide tax advantages, as the interest earned can be exempt from federal and state taxes. Securitized bonds are created by pooling various types of debt, such as mortgages or loans, and selling the consolidated debt as bonds to investors.`,
        });
      } catch (error) {
        console.error('error', error);
      }
    },
    createFixedIncomeDurationBreakdownArea(portfolio, benchmark, optimizedPortfolio) {
      try {
        const { chartData, ...rest } = getFixedIncomeDurationBreakdown(portfolio, benchmark, optimizedPortfolio, this.advisorStore);
        this.checkupStore.pushToAreas({
          name: 'Fixed Income Maturity Breakdown',
          ...rest,
          clickthroughFunction: this.checkupStore.getFixedIncomeMaturityLookthrough,
          chartData: [{
            ...chartData,
            type: 'bar',
            options: {
              scales: {
                y: {
                  title: { display: true, text: '% of Portfolio Allocation' },
                },
              },
            },
            formats: { y: 'percent' },
          }],
          headerText: `Fixed income maturity refers to the length of time until the principal amount of a bond is repaid. Bonds are typically categorized based on their maturity dates into short-term, medium-term, and long-term bonds. Short-term bonds mature in less than three years, medium-term bonds mature in three to ten years, and long-term bonds mature in more than ten years. The maturity breakdown of a portfolio helps investors understand the timeline for when their investments will be repaid and how interest rate changes might impact their portfolio. Short-term bonds generally have lower interest rate risk and offer more liquidity. In contrast, long-term bonds might provide higher yields but are more sensitive to interest rate changes; for example, if interest rates rise, the prices of long-term bonds will typically fall more significantly than those of short-term bonds. `,
        });
      } catch (error) {
        console.error('error', error);
      }
    },
    createStatisticsArea(portfolio, benchmark, optimizedPortfolio) {
      // load, Alpha, Beta, Sharpe, Rsquared, and Stdev
      const extraData = [];
      const current = [];
      const proposal = [];
      const optimized = [];

      const getMetrics = portfolio => [
        {
          label: 'Yield',
          rawValue: portfolio.sector_market_cap_asset_class_breakdown?.average_yield ?? null,
          formattedValue: portfolio.sector_market_cap_asset_class_breakdown?.average_yield != null ?
                portfolio.sector_market_cap_asset_class_breakdown.average_yield.toFixed(2) + '%' :
                '',
        },
        {
          label: 'Equity Dividend Yield',
          rawValue: portfolio.sector_market_cap_asset_class_breakdown?.average_equity_dividend_yield ?? null,
          formattedValue: portfolio.sector_market_cap_asset_class_breakdown?.average_equity_dividend_yield != null ?
                portfolio.sector_market_cap_asset_class_breakdown.average_equity_dividend_yield.toFixed(2) + '%' :
                '',
        },
        {
          label: 'Fixed Income YTM',
          rawValue: portfolio.sector_market_cap_asset_class_breakdown?.average_fi_ytm ?? null,
          formattedValue: portfolio.sector_market_cap_asset_class_breakdown?.average_fi_ytm != null ?
                portfolio.sector_market_cap_asset_class_breakdown.average_fi_ytm.toFixed(2) + '%' :
                '',
        },
        {
          label: 'Debt to Equity',
          rawValue: portfolio.sector_market_cap_asset_class_breakdown?.average_debt_to_equity ?? null,
          formattedValue: portfolio.sector_market_cap_asset_class_breakdown?.average_debt_to_equity != null ?
                portfolio.sector_market_cap_asset_class_breakdown.average_debt_to_equity.toFixed(2) :
                '',
        },
        {
          label: 'Forward PE Ratio',
          rawValue: portfolio.sector_market_cap_asset_class_breakdown?.average_forward_pe ?? null,
          formattedValue: portfolio.sector_market_cap_asset_class_breakdown?.average_forward_pe != null ?
                portfolio.sector_market_cap_asset_class_breakdown.average_forward_pe.toFixed(2) :
                '',
        },
        {
          label: 'Expense Ratio',
          rawValue: portfolio.average_expense_ratio != null ? portfolio.average_expense_ratio * 100 : null,
          formattedValue: portfolio.average_expense_ratio != null ? (portfolio.average_expense_ratio * 100).toFixed(2) + '%' : 'N/A',
        },
        {
          label: 'Turnover',
          rawValue: portfolio.average_turnover?.[0] != null ? portfolio.average_turnover[0] * 100 : null,
          formattedValue: portfolio.average_turnover?.[0] != null ? (portfolio.average_turnover[0] * 100).toFixed(2) + '%' : 'N/A',
        },
        {
          label: 'Avg # Hldgs (funds)',
          rawValue: portfolio.sector_market_cap_asset_class_breakdown?.average_num_holdings ?? null,
          formattedValue: portfolio.sector_market_cap_asset_class_breakdown?.average_num_holdings != null ?
                portfolio.sector_market_cap_asset_class_breakdown.average_num_holdings.toFixed(0) : 'N/A',
        },
        {
          label: '3 Yr Mean Returns',
          rawValue: portfolio.trailing_returns_monthly_rebalance?.[0]?.trailing_3_yr_returns != null ? portfolio.trailing_returns_monthly_rebalance[0].trailing_3_yr_returns * 100 : null,
          formattedValue: portfolio.trailing_returns_monthly_rebalance?.[0]?.trailing_3_yr_returns != null ?
                (portfolio.trailing_returns_monthly_rebalance[0].trailing_3_yr_returns * 100).toFixed(2) + '%' : 'N/A',
        },
        {
          label: '3 Yr Standard Deviation',
          rawValue: portfolio.trailing_volatilities?.[0]?.trailing_3_yr_volatility != null ? portfolio.trailing_volatilities[0].trailing_3_yr_volatility * 100 : null,
          formattedValue: portfolio.trailing_volatilities?.[0]?.trailing_3_yr_volatility != null ?
                (portfolio.trailing_volatilities[0].trailing_3_yr_volatility * 100).toFixed(2) + '%' : 'N/A',
        },
        {
          label: '5 Yr Mean Returns',
          rawValue: portfolio.trailing_returns_monthly_rebalance?.[0]?.trailing_5_yr_returns != null ? portfolio.trailing_returns_monthly_rebalance[0].trailing_5_yr_returns * 100 : null,
          formattedValue: portfolio.trailing_returns_monthly_rebalance?.[0]?.trailing_5_yr_returns != null ?
                (portfolio.trailing_returns_monthly_rebalance[0].trailing_5_yr_returns * 100).toFixed(2) + '%' : 'N/A',
        },
        {
          label: '5 Yr Standard Deviation',
          rawValue: portfolio.trailing_volatilities?.[0]?.trailing_5_yr_volatility != null ? portfolio.trailing_volatilities[0].trailing_5_yr_volatility * 100 : null,
          formattedValue: portfolio.trailing_volatilities?.[0]?.trailing_5_yr_volatility != null ?
                (portfolio.trailing_volatilities[0].trailing_5_yr_volatility * 100).toFixed(2) + '%' : 'N/A',
        },
        {
          label: '10 Yr Mean Returns',
          rawValue: portfolio.trailing_returns_monthly_rebalance?.[0]?.trailing_10_yr_returns != null ? portfolio.trailing_returns_monthly_rebalance[0].trailing_10_yr_returns * 100 : null,
          formattedValue: portfolio.trailing_returns_monthly_rebalance?.[0]?.trailing_10_yr_returns != null ?
                (portfolio.trailing_returns_monthly_rebalance[0].trailing_10_yr_returns * 100).toFixed(2) + '%' : 'N/A',
        },
        {
          label: '10 Yr Standard Deviation',
          rawValue: portfolio.trailing_volatilities?.[0]?.trailing_10_yr_volatility != null ? portfolio.trailing_volatilities[0].trailing_10_yr_volatility * 100 : null,
          formattedValue: portfolio.trailing_volatilities?.[0]?.trailing_10_yr_volatility != null ?
                (portfolio.trailing_volatilities[0].trailing_10_yr_volatility * 100).toFixed(2) + '%' : 'N/A',
        },
      ];

      // Portfolio
      if (this.checkupStore.portfolio) {
        const metrics = getMetrics(this.checkupStore.portfolio);
        for (const metric of metrics) {
          extraData.push({
            name: `${this.checkupStore.portfolio.name || 'Portfolio'}`,
            label: metric.label,
            value: metric.formattedValue,
          });
          current.push({
            name: metric.label,
            value: metric.rawValue,
          });
        }
      }

      // Benchmark
      if (this.checkupStore.benchmark) {
        const metrics = getMetrics(this.checkupStore.benchmark);
        for (const metric of metrics) {
          extraData.push({
            name: `${this.checkupStore.benchmark.name || 'Benchmark'}`,
            label: metric.label,
            value: metric.formattedValue,
          });
          proposal.push({
            name: metric.label,
            value: metric.rawValue,
          });
        }
      }

      // Optimized Portfolio
      if (this.checkupStore.optimizedPortfolio) {
        const metrics = getMetrics(this.checkupStore.optimizedPortfolio);
        for (const metric of metrics) {
          extraData.push({
            name: `${this.checkupStore.optimizedPortfolio.name || 'Optimized'}`,
            label: metric.label,
            value: metric.formattedValue,
          });
          optimized.push({
            name: metric.label,
            value: metric.rawValue,
          });
        }
      }

      this.checkupStore.pushToAreas({
        name: 'Statistics',
        methodology: 'StatisticsMethodology',
        clickthroughFunction: this.checkupStore.getStatisticsLookthrough,
        extraData,
        headerText: `The statistics table highlights portfolio characteristics and performance metrics. Yield indicates how much income a portfolio generates through dividends and interest payments. Turnover reflects how frequently the portfolio's holdings are traded, impacting tax efficiency and transaction costs. The number of holdings indicates diversification, with more holdings generally reducing risk from individual assets. Expense ratio measures the cost of managing the portfolio. Performance metrics provide insight into the portfolio's historical volatility and returns over different time periods.`,
        current,
        proposal,
        optimized,
        isPercent: true,
      });
    },
    filterInclusionSymbols(value) {
      this.filteredInclusionSymbols = this.checkupStore.availableSymbols.filter(symbol => symbol.toUpperCase().includes(value.toUpperCase()));
    },
    filterExclusionSymbols(value) {
      this.filteredExclusionSymbols = this.checkupStore.availableSymbols.filter(symbol => symbol.toUpperCase().includes(value.toUpperCase()));
    },
    filterDoNotBuySellSymbols(value) {
      this.filteredDoNotBuySellSymbols = this.checkupStore.availableSymbols.filter(symbol => symbol.toUpperCase().includes(value.toUpperCase()));
      this.filteredDoNotBuySellSymbols.sort((x, y) => {
        if (x.length < y.length) {
          return true;
        } else if (x.length > y.length) {
          return false;
        } else {
          return (x < y);
        }
      });
    },
  },

};
</script>


<style scoped>
  .modal {
    display: flex;
    justify-content: center;
    align-items: center;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0,0,0,0.5);
  }
  .modal-content {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    max-width: 500px;
  }
  .close {
    position: absolute;
    top: 10px;
    right: 15px;
    cursor: pointer;
  }


  .page {
    /* Center the page on the screen */
    border: 1px solid #000;
    /* Define a border so you can see the page's dimensions */
    background-color: #fff;
    /* Set the background color to white */
    box-sizing: border-box;
    /* Include padding and border in element's total width and height */
    overflow: visible;
  }
  .pagewidth {
    max-width: 70em;
    min-width: 50em;
    margin: auto;

  }

  .pagecontent {
    padding: 0.5in;
    /* Provide a bit of space around the content */
  }

  .niceblue {
    background-color: #0a0b4f;
    background-image: linear-gradient(141deg, #1e4182 0, #0a0b4f 71%, #20217e 100%);
    color: white;
  }
  .pro-blue {
    background-color: rgb(15, 7, 43);
    background-image: linear-gradient(141deg, rgb(15, 7, 43), rgb(33, 13, 100), rgb(18, 9, 53));
    color: white;
  }

  .niceblue>p,
  .niceblue>.title,
  .niceblue>.subtitle {
    color: white;
  }

  .highlightbox {
    border: 2px solid #3C60E2;
    border-radius: 5px;
    padding: 10px;
  }


  .paperwidth {
    margin: auto;
    width: 8.5in;

  }

  .stickysection {
    position: sticky;
    top: 0;  /* This is crucial - defines where it sticks */
    z-index: 10;
    border: 2px solid white;
    border-radius: 5px;
    background-color: inherit;
    /* Add height constraints */
    max-height: 80vh;
    padding: 1rem;
  }
  .pagewidth {
    max-width: 80em;
    min-width: 50em;
  }
  .greentop {
    border-top: 3px solid green;
  }
  .yellowtop {
    border-top: 3px solid yellow;
  }
  .redtop {
    border-top: 3px solid red;
  }
  .tabs.color-tips  li {
    margin-right: .5em;
  }
  .tabs.color-tips  a {
    border-top: 3px solid green;
  }
  .sticky-bottom {
    position: fixed;
    bottom: 0;
    width: 100%;
}
.whitebackground {
  background-color: white;
  color: black;
}
.off-white-background {
  background-color: whitesmoke;
  color: black;
}
.chatborder {
  border: 3px solid purple;
  border-radius: 9px;
  padding: .5em;
}
.tightcolumns > .columns, .tightcolumns > .columns > .column {
  margin: 0;
  padding: 0
}
  </style>
